import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState, type FormEvent } from 'react';

import type { PermissionAreaId, UserDto, UserGrant, UserRole } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatShortDateTime } from '../lib/format';

const ROLES: { id: UserRole; label: string; hint: string }[] = [
  { id: 'owner', label: 'владелец', hint: 'всё, включая настройки и права других' },
  { id: 'assistant', label: 'пользователь', hint: 'читает всё, пишет там, где отмечено' },
  { id: 'viewer', label: 'только чтение', hint: 'ничего не пишет, отметки не действуют' },
];

/**
 * Areas in display order with their names; modules carry the grant of their writing tools.
 * The type checks that every area is here.
 */
const AREAS: { id: PermissionAreaId; label: string; hint: string; grant?: UserGrant }[] = [
  {
    id: 'calendar',
    label: 'Календарь',
    hint: 'события, гости, ссылки на запись',
    grant: 'calendar:write',
  },
  {
    id: 'meetings',
    label: 'Встречи',
    hint: 'Zoom, Google Meet; schedule_call — с календарём',
    grant: 'meetings:write',
  },
  { id: 'labs', label: 'Анализы', hint: 'отчёты лабораторий', grant: 'labs:write' },
  { id: 'notes', label: 'Заметки', hint: 'уточнения для модели', grant: 'notes:write' },
  {
    id: 'messaging',
    label: 'Telegram',
    hint: 'архив читают все; отправка контактам',
    grant: 'messaging:write',
  },
  { id: 'pages', label: 'Страницы', hint: 'публикация по ссылке', grant: 'pages:write' },
  {
    id: 'transcripts',
    label: 'Расшифровки',
    hint: 'голосовые заметки и звонки',
    grant: 'transcripts:write',
  },
  {
    id: 'documents',
    label: 'Документы',
    hint: 'направления, счета, заключения врачей',
    grant: 'documents:write',
  },
  {
    id: 'apartments',
    label: 'Квартиры',
    hint: 'доступ, контакты, платежи, документы',
    grant: 'apartments:write',
  },
  { id: 'connections', label: 'Подключения', hint: 'WHOOP, Google' },
  { id: 'users', label: 'Пользователи', hint: 'роли и права' },
  { id: 'telegram', label: 'Аккаунты Telegram', hint: 'вход, архив, админы' },
  { id: 'schedule', label: 'Расписание', hint: 'вечернее сообщение' },
  { id: 'audit', label: 'Журнал', hint: 'аудит операций' },
];
// Every PermissionAreaId occurs above: assigning the ids to this record fails otherwise.
const _areaCheck: Record<PermissionAreaId, true> = Object.fromEntries(
  AREAS.map((a) => [a.id, true]),
) as Record<PermissionAreaId, true>;
void _areaCheck;

function useUserChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (change: () => Promise<unknown>) => change(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['users'] }),
  });
}

/** Column header: who, the role (changeable, except one's own), the last sign-in. */
function UserHeader({ user, self }: { user: UserDto; self: boolean }) {
  const change = useUserChange();
  return (
    <th scope="col" className="users__user">
      <div className="users__name">
        {user.displayName ?? user.username}
        {user.displayName && <span className="muted small"> {user.username}</span>}
        {user.disabled && <span className="pill pill--muted">отключён</span>}
      </div>
      <select
        aria-label={`Роль ${user.username}`}
        value={user.role}
        disabled={self || change.isPending}
        title={self ? 'Свою роль здесь менять нельзя' : ROLES.find((r) => r.id === user.role)?.hint}
        onChange={(e) => {
          // Read now: by the time the mutation runs, React has put the old value back.
          const role = e.target.value as UserRole;
          change.mutate(() => api.setRole(user.username, { role }));
        }}
      >
        {ROLES.map((r) => (
          <option key={r.id} value={r.id} title={r.hint}>
            {r.label}
          </option>
        ))}
      </select>
      <span className="muted small">
        {user.lastLoginAt ? `вход ${formatShortDateTime(user.lastLoginAt)}` : 'ещё не входил'}
      </span>
      {!self && (
        <div className="users__actions">
          <button
            type="button"
            className="btn btn--small btn--ghost"
            disabled={change.isPending}
            title={
              user.disabled
                ? 'Снова разрешить вход'
                : 'Закрыть вход и отозвать все токены; учётка и её права остаются'
            }
            onClick={() =>
              change.mutate(() =>
                api.setDisabled(user.username, { disabled: !user.disabled }).then(() => undefined),
              )
            }
          >
            {user.disabled ? 'Включить' : 'Отключить'}
          </button>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            disabled={change.isPending}
            onClick={() => {
              if (
                window.confirm(
                  `Удалить учётку ${user.username}? Все её входы и коннекторы перестанут работать.`,
                )
              ) {
                change.mutate(() => api.removeUser(user.username).then(() => undefined));
              }
            }}
          >
            Удалить
          </button>
        </div>
      )}
      {change.isError && <span className="text-error small">{change.error.message}</span>}
    </th>
  );
}

const MIN_PASSWORD = 12;

/** A new account: the owner types the login and the first password; the user changes it later. */
function AddUserForm() {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<UserRole>('assistant');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const add = useMutation({
    mutationFn: () =>
      api.addUser({
        username: username.trim(),
        password,
        role,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
      }),
    onSuccess: () => {
      setUsername('');
      setDisplayName('');
      setPassword('');
      setRepeat('');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
  const mismatch = repeat.length > 0 && password !== repeat;
  const ready = username.trim().length >= 2 && password.length >= MIN_PASSWORD && !mismatch;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) add.mutate();
  };
  return (
    <form className="card form form--wide" onSubmit={submit}>
      <div className="card__header">
        <h3>Новая учётка</h3>
      </div>
      <div className="form__row">
        <label>
          Логин <span className="muted small">(латиница, цифры, . - _)</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="off"
            required
            minLength={2}
            maxLength={32}
          />
        </label>
        <label>
          Имя <span className="muted small">(необязательно)</span>
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={100}
          />
        </label>
        <label>
          Роль
          <select value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            {ROLES.map((r) => (
              <option key={r.id} value={r.id} title={r.hint}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="form__row">
        <label>
          Пароль <span className="muted small">(от {MIN_PASSWORD} символов)</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={MIN_PASSWORD}
            required
          />
        </label>
        <label>
          Пароль ещё раз
          <input
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(e) => setRepeat(e.target.value)}
            required
          />
        </label>
      </div>
      {mismatch && <p className="text-error small">Пароли не совпадают.</p>}
      {add.isError && <p className="text-error small">{add.error.message}</p>}
      {add.isSuccess && (
        <p className="small">
          Учётка {add.data.username} создана. Передайте пароль лично; пользователь сменит его в
          Настройках → Учётная запись.
        </p>
      )}
      <div className="actions-row">
        <button type="submit" className="btn btn--primary" disabled={!ready || add.isPending}>
          {add.isPending ? 'Создаю…' : 'Создать'}
        </button>
      </div>
    </form>
  );
}

/**
 * One cell. A module row: the checkbox "запись" (reading is open to everyone); the owner has it
 * always, a read-only account cannot have it. An owner's section: a tick or a dash.
 */
function PermissionCell({ user, area }: { user: UserDto; area: (typeof AREAS)[number] }) {
  const change = useUserChange();
  const level = user.permissions[area.id];
  const grant = area.grant;
  if (!grant) {
    return (
      <td className="users__cell">
        {level === 'manage' ? (
          <span className="pill pill--ok">✓ владелец</span>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    );
  }
  if (user.role === 'owner') {
    return (
      <td className="users__cell">
        <span className="pill pill--ok" title="Владелец пишет везде">
          ✓ запись
        </span>
      </td>
    );
  }
  if (user.role === 'viewer') {
    return (
      <td className="users__cell">
        <span
          className="muted small"
          title="Роль «только чтение»: сначала смените роль на «пользователь»"
        >
          только чтение
        </span>
      </td>
    );
  }
  const enabled = level === 'write';
  return (
    <td className="users__cell">
      <label className="toggle" title={`Право ${grant}`}>
        <input
          type="checkbox"
          checked={enabled}
          disabled={change.isPending}
          onChange={(e) => {
            // Read now: by the time the mutation runs, React has put the old value back.
            const next = e.target.checked;
            change.mutate(() => api.setGrant(user.username, { grant, enabled: next }));
          }}
        />
        <span className={enabled ? 'pill pill--ok' : 'pill pill--muted'}>
          {enabled ? 'запись' : 'чтение'}
        </span>
      </label>
      {change.isError && <div className="text-error small">{change.error.message}</div>}
    </td>
  );
}

/**
 * Settings → Users (owner only): who may do what, area by area. The role, disabling and
 * deleting are in the column header, grants in their cells, new accounts in the form below;
 * every change is audited. One's own account is changed by another owner or `pnpm users`.
 */
export function UsersSection() {
  const query = useQuery({ queryKey: ['users'], queryFn: api.users });
  const me = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 60_000 });
  const [adding, setAdding] = useState(false);
  const users = query.data?.users ?? [];
  const self = me.data?.mode === 'session' ? me.data.user.name : undefined;
  return (
    <section>
      <div className="page__header">
        <h2>Пользователи</h2>
        {query.data?.available && (
          <button
            type="button"
            className="btn btn--small btn--primary"
            onClick={() => setAdding((v) => !v)}
          >
            {adding ? 'Скрыть форму' : 'Добавить'}
          </button>
        )}
      </div>
      <p className="muted">
        Читать все разделы могут все учётки. Галочка в строке раздела даёт право записи в него:
        создавать, менять, удалять. Роль «владелец» пишет везде и управляет настройками, «только
        чтение» не пишет ничего (галочки недоступны), «пользователь» пишет там, где отмечено. Каждая
        запись подтверждается и попадает в журнал. После смены прав приложению Claude нужно
        переподключить коннектор.
      </p>
      {adding && <AddUserForm />}
      {query.isPending && <p className="muted">Загрузка…</p>}
      {query.isError && <p className="text-error">{query.error.message}</p>}
      {query.data && !query.data.available && <p className="muted">Учётки недоступны: нет базы.</p>}
      {query.data?.available && users.length === 0 && (
        <p className="muted">Учёток пока нет — добавьте первую.</p>
      )}
      {users.length > 0 && (
        <div className="card card--flush table-scroll">
          <table className="table users">
            <thead>
              <tr>
                <th scope="col">Раздел</th>
                {users.map((u) => (
                  <UserHeader key={u.username} user={u} self={u.username === self} />
                ))}
              </tr>
            </thead>
            <tbody>
              {AREAS.map((area, index) => (
                <Fragment key={area.id}>
                  {index === 0 && (
                    <tr className="users__group">
                      <th scope="rowgroup" colSpan={users.length + 1}>
                        Разделы с данными: читают все, запись по галочке
                      </th>
                    </tr>
                  )}
                  {!area.grant && AREAS[index - 1]?.grant && (
                    <tr className="users__group">
                      <th scope="rowgroup" colSpan={users.length + 1}>
                        Настройки: только владелец
                      </th>
                    </tr>
                  )}
                  <tr>
                    <th scope="row" className="users__area">
                      {area.label}
                      <span className="muted small">{area.hint}</span>
                    </th>
                    {users.map((u) => (
                      <PermissionCell key={u.username} user={u} area={area} />
                    ))}
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
